import React from 'react';
import { ArrowLeft } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const PrivacyPolicy = ({ onBackToHome }) => {
  const { t, i18n } = useTranslation();
  const isTr = i18n.language === 'tr';

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm">
        <div className="container mx-auto px-6 py-4">
          <button
            onClick={onBackToHome}
            className="flex items-center gap-2 text-gray-600 hover:text-blue-600 transition-colors"
            data-testid="back-to-home-btn"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>{isTr ? 'Ana Sayfaya Dön' : 'Back to Home'}</span>
          </button>
        </div>
      </div>

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-900 via-blue-800 to-blue-700 text-white py-16">
        <div className="container mx-auto px-6 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-4" data-testid="privacy-title">
            {t('pages.privacy.title')}
          </h1>
          <p className="text-blue-100">{t('pages.privacy.lastUpdated')}: {isTr ? 'Aralık 2025' : 'December 2025'}</p>
        </div>
      </section>

      {/* Content */}
      <section className="py-16">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto bg-white rounded-xl shadow-md p-8 md:p-12">
            <div className="prose prose-lg max-w-none">
              {isTr ? (
                <>
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">1. Giriş</h2>
                  <p className="text-gray-600 mb-6">
                    CoachMeUpp ("biz", "bizim" veya "bize") gizliliğinizi korumaya kararlıdır. Bu Gizlilik Politikası, web sitemizi ziyaret ettiğinizde veya dijital koçluk platformumuzu kullandığınızda bilgilerinizi nasıl topladığımızı, kullandığımızı, açıkladığımızı ve koruduğumuzu açıklar.
                  </p>

                  <h2 className="text-2xl font-bold text-gray-900 mb-4">2. Topladığımız Bilgiler</h2>
                  <p className="text-gray-600 mb-4">Hakkınızda çeşitli yollarla bilgi toplayabiliriz:</p>
                  <ul className="list-disc pl-6 text-gray-600 mb-6 space-y-2">
                    <li><strong>Kişisel Veriler:</strong> Formları doldururken veya hizmetlerimize kaydolurken sağladığınız ad, e-posta adresi, telefon numarası, şirket adı, iş unvanı ve diğer iletişim bilgileri.</li>
                    <li><strong>Kullanım Verileri:</strong> IP adresiniz, tarayıcı türünüz, ziyaret edilen sayfalar, sayfalarda geçirilen süre ve diğer tanı verileri dahil olmak üzere platformumuza nasıl eriştiğiniz ve kullandığınız hakkında bilgiler.</li>
                    <li><strong>Koçluk Oturumu Verileri:</strong> Sıkı gizlilik içinde işlenen notlar, hedefler, ilerleme ölçümleri ve koçluk oturumlarınızla ilgili diğer bilgiler.</li>
                  </ul>

                  <h2 className="text-2xl font-bold text-gray-900 mb-4">3. Bilgilerinizi Nasıl Kullanıyoruz</h2>
                  <p className="text-gray-600 mb-4">Topladığımız bilgileri şu amaçlarla kullanıyoruz:</p>
                  <ul className="list-disc pl-6 text-gray-600 mb-6 space-y-2">
                    <li>Koçluk platformumuzu ve hizmetlerimizi sağlamak, işletmek ve sürdürmek</li>
                    <li>İhtiyaçlarınıza ve tercihlerinize göre sizi uygun koçlarla eşleştirmek</li>
                    <li>Gelişim ilerlemenizi izlemek ve analiz raporları oluşturmak</li>
                    <li>Hesabınız, hizmetler ve destek hakkında sizinle iletişim kurmak</li>
                    <li>Size pazarlama iletişimleri göndermek (izninizle)</li>
                    <li>Platformumuzu geliştirmek ve yeni özellikler geliştirmek</li>
                    <li>Yasal yükümlülüklere uymak</li>
                  </ul>

                  <h2 className="text-2xl font-bold text-gray-900 mb-4">4. Veri Paylaşımı ve Açıklama</h2>
                  <p className="text-gray-600 mb-6">
                    Kişisel bilgilerinizi satmıyoruz. Bilgilerinizi şu kişilerle paylaşabiliriz:
                  </p>
                  <ul className="list-disc pl-6 text-gray-600 mb-6 space-y-2">
                    <li><strong>Koçlar:</strong> Atanan koçunuz(larınız), koçluk hizmetleri sağlamak için ilgili bilgilere erişebilir.</li>
                    <li><strong>Organizasyonunuz:</strong> İşvereniniz koçluğunuzu sponsorluk yapıyorsa, toplu ve anonimleştirilmiş ilerleme raporları alabilir.</li>
                    <li><strong>Hizmet Sağlayıcılar:</strong> Platformumuzu işletmemize yardımcı olan üçüncü taraf satıcılar (barındırma, analitik, ödeme işleme).</li>
                    <li><strong>Yasal Gereklilikler:</strong> Yasa gerektirdiğinde veya haklarımızı ve güvenliğimizi korumak için.</li>
                  </ul>

                  <h2 className="text-2xl font-bold text-gray-900 mb-4">5. Veri Güvenliği</h2>
                  <p className="text-gray-600 mb-6">
                    Şifreleme, güvenli sunucular ve erişim kontrolleri dahil olmak üzere kişisel bilgilerinizi korumak için uygun teknik ve organizasyonel güvenlik önlemleri uyguluyoruz. Ancak, internet üzerinden hiçbir iletim yöntemi %100 güvenli değildir ve mutlak güvenliği garanti edemeyiz.
                  </p>

                  <h2 className="text-2xl font-bold text-gray-900 mb-4">6. Haklarınız</h2>
                  <p className="text-gray-600 mb-4">Konumunuza bağlı olarak şu haklara sahip olabilirsiniz:</p>
                  <ul className="list-disc pl-6 text-gray-600 mb-6 space-y-2">
                    <li>Hakkınızda tuttuğumuz kişisel bilgilere erişim</li>
                    <li>Yanlış bilgilerin düzeltilmesini talep etme</li>
                    <li>Kişisel bilgilerinizin silinmesini talep etme</li>
                    <li>Verilerinizin belirli işlenmesine itiraz etme veya kısıtlama</li>
                    <li>Verilerinizin taşınabilirliğini talep etme</li>
                    <li>İşlemenin izne dayandığı durumlarda izni geri çekme</li>
                  </ul>

                  <h2 className="text-2xl font-bold text-gray-900 mb-4">7. İletişim</h2>
                  <p className="text-gray-600 mb-6">
                    Bu Gizlilik Politikası veya veri uygulamalarımız hakkında sorularınız varsa, lütfen bizimle iletişime geçin:
                  </p>
                  <div className="bg-gray-50 p-6 rounded-lg">
                    <p className="text-gray-700 font-semibold">CoachMeUpp</p>
                    <p className="text-gray-600">E-posta: bilgi@coachmeupp.com</p>
                    <p className="text-gray-600">Telefon: +90 533 763 53 88</p>
                    <p className="text-gray-600">Konum: Ankara, Türkiye</p>
                  </div>
                </>
              ) : (
                <>
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">1. Introduction</h2>
                  <p className="text-gray-600 mb-6">
                    CoachMeUpp ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website or use our digital coaching platform.
                  </p>

                  <h2 className="text-2xl font-bold text-gray-900 mb-4">2. Information We Collect</h2>
                  <p className="text-gray-600 mb-4">We may collect information about you in various ways, including:</p>
                  <ul className="list-disc pl-6 text-gray-600 mb-6 space-y-2">
                    <li><strong>Personal Data:</strong> Name, email address, phone number, company name, job title, and other contact information you provide when filling out forms or registering for our services.</li>
                    <li><strong>Usage Data:</strong> Information about how you access and use our platform, including your IP address, browser type, pages visited, time spent on pages, and other diagnostic data.</li>
                    <li><strong>Coaching Session Data:</strong> Notes, goals, progress metrics, and other information related to your coaching sessions, which are treated with strict confidentiality.</li>
                  </ul>

                  <h2 className="text-2xl font-bold text-gray-900 mb-4">3. How We Use Your Information</h2>
                  <p className="text-gray-600 mb-4">We use the information we collect to:</p>
                  <ul className="list-disc pl-6 text-gray-600 mb-6 space-y-2">
                    <li>Provide, operate, and maintain our coaching platform and services</li>
                    <li>Match you with appropriate coaches based on your needs and preferences</li>
                    <li>Track your development progress and generate analytics reports</li>
                    <li>Communicate with you about your account, services, and support</li>
                    <li>Send you marketing communications (with your consent)</li>
                    <li>Improve our platform and develop new features</li>
                    <li>Comply with legal obligations</li>
                  </ul>

                  <h2 className="text-2xl font-bold text-gray-900 mb-4">4. Data Sharing and Disclosure</h2>
                  <p className="text-gray-600 mb-6">
                    We do not sell your personal information. We may share your information with:
                  </p>
                  <ul className="list-disc pl-6 text-gray-600 mb-6 space-y-2">
                    <li><strong>Coaches:</strong> Your assigned coach(es) will have access to relevant information to provide coaching services.</li>
                    <li><strong>Your Organization:</strong> If your employer sponsors your coaching, they may receive aggregated and anonymized progress reports.</li>
                    <li><strong>Service Providers:</strong> Third-party vendors who help us operate our platform (hosting, analytics, payment processing).</li>
                    <li><strong>Legal Requirements:</strong> When required by law or to protect our rights and safety.</li>
                  </ul>

                  <h2 className="text-2xl font-bold text-gray-900 mb-4">5. Data Security</h2>
                  <p className="text-gray-600 mb-6">
                    We implement appropriate technical and organizational security measures to protect your personal information, including encryption, secure servers, and access controls. However, no method of transmission over the internet is 100% secure, and we cannot guarantee absolute security.
                  </p>

                  <h2 className="text-2xl font-bold text-gray-900 mb-4">6. Your Rights</h2>
                  <p className="text-gray-600 mb-4">Depending on your location, you may have the right to:</p>
                  <ul className="list-disc pl-6 text-gray-600 mb-6 space-y-2">
                    <li>Access the personal information we hold about you</li>
                    <li>Request correction of inaccurate information</li>
                    <li>Request deletion of your personal information</li>
                    <li>Object to or restrict certain processing of your data</li>
                    <li>Request portability of your data</li>
                    <li>Withdraw consent where processing is based on consent</li>
                  </ul>

                  <h2 className="text-2xl font-bold text-gray-900 mb-4">7. Contact Us</h2>
                  <p className="text-gray-600 mb-6">
                    If you have any questions about this Privacy Policy or our data practices, please contact us at:
                  </p>
                  <div className="bg-gray-50 p-6 rounded-lg">
                    <p className="text-gray-700 font-semibold">CoachMeUpp</p>
                    <p className="text-gray-600">Email: bilgi@coachmeupp.com</p>
                    <p className="text-gray-600">Phone: +90 533 763 53 88</p>
                    <p className="text-gray-600">Location: Ankara, Turkey</p>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PrivacyPolicy;
